import { useCallback, useMemo } from 'react';

import { BaseTabFormData, FeatureMode, FeatureTabRef, SubmitDateRange } from '../types/types';

export const SAVE_FLOW_CONTROL_ERRORS = {
  ConflictResolutionRequired: 'CONFLICT_RESOLUTION_REQUIRED',
  SaveCompleted: 'SAVE_COMPLETED',
} as const;

/**
 * Parameters for feature details form manager hook
 */
export interface FeatureDetailsFormManagerParams<
  TEntity extends { id: string | number } = { id: string | number },
  TFormData extends BaseTabFormData = BaseTabFormData,
> {
  mode: FeatureMode;
  currentTab: string;
  tabRefs: Record<string, React.RefObject<FeatureTabRef<TFormData>>>;
  entityData?: TEntity;
  currentTabHasChanges: boolean;
  hasDraftData: boolean;

  // Draft management functions
  getDraftData: (tab: string) => Partial<TFormData> | undefined;
  saveDraftData: (params: { tab: string; data: TFormData }) => void;
  clearDraft: () => void;
  getFinalSubmissionData: () => TFormData;

  // Entity-specific save handler
  handleSave: (params: { data: TFormData; successCallback?: () => void }) => void;

  // Optional: Custom validation logic for hasAnyData in create mode
  calculateHasAnyData?: (context: {
    mode: FeatureMode;
    currentTab: string;
    tabRefs: Record<string, React.RefObject<FeatureTabRef<TFormData>>>;
    getDraftData: (tab: string) => Partial<TFormData> | undefined;
    hasDraftData: boolean;
  }) => boolean;
}

/**
 * Return type for feature details form manager hook
 */
export interface FeatureDetailsFormManagerReturn<
  TFormData extends BaseTabFormData = BaseTabFormData,
> {
  hasAnyUnsavedChanges: boolean;
  hasAnyData: boolean;
  validateCurrentTab: (dateRange?: SubmitDateRange) => Promise<void>;
  getCurrentTabFormData: () => TFormData | null;
  saveCurrentTabData: () => Promise<void>;
  /** Submit handler - receives optional date range from footer in Edit/EditChange modes */
  handleFormSubmit: (dateRange?: SubmitDateRange) => Promise<void>;
}

/**
 * Feature details form manager hook for coordinating multiple tab forms.
 * Manages draft persistence, validation, and submission across feature detail tabs.
 *
 * This hook handles:
 * - Form state calculations (hasAnyUnsavedChanges, hasAnyData)
 * - Tab validation using imperative refs
 * - Form data retrieval from current tab
 * - Draft data persistence
 * - Form submission flow for both create and edit modes
 *
 * @example
 * ```typescript
 * const featureFormManager = useFeatureDetailsFormManager({
 *   mode,
 *   currentTab,
 *   tabRefs: { info: infoRef, billing: billingRef },
 *   entityData: projectData,
 *   currentTabHasChanges,
 *   hasDraftData,
 *   getDraftData,
 *   saveDraftData,
 *   clearDraft,
 *   getFinalSubmissionData,
 *   handleSave: handleSaveProject,
 *   calculateHasAnyData: (context) => {
 *     // Custom validation logic for create mode
 *     return hasValidRequiredFields(context);
 *   }
 * });
 * ```
 */
export function useFeatureDetailsFormManager<
  TEntity extends { id: string | number } = { id: string | number },
  TFormData extends BaseTabFormData = BaseTabFormData,
>({
  mode,
  currentTab,
  tabRefs,
  entityData,
  currentTabHasChanges,
  hasDraftData,
  getDraftData,
  saveDraftData,
  clearDraft,
  getFinalSubmissionData,
  handleSave,
  calculateHasAnyData,
}: FeatureDetailsFormManagerParams<
  TEntity,
  TFormData
>): FeatureDetailsFormManagerReturn<TFormData> {
  /**
   * Determine if there are unsaved changes across the form.
   * Combines current tab changes with draft data presence in create mode.
   */
  const hasAnyUnsavedChanges = useMemo(() => {
    return currentTabHasChanges || (mode === FeatureMode.Create && hasDraftData);
  }, [currentTabHasChanges, mode, hasDraftData]);

  /**
   * Calculate if the form has any valid data.
   * Uses custom calculation logic if provided, otherwise falls back to currentTabHasChanges.
   */
  const hasAnyData = useMemo(() => {
    if (calculateHasAnyData) {
      return calculateHasAnyData({
        mode,
        currentTab,
        tabRefs,
        getDraftData,
        hasDraftData,
      });
    }

    // Default fallback logic
    if (mode === FeatureMode.Create) {
      return hasDraftData; // For create mode, any draft data indicates valid data
    }

    return currentTabHasChanges; // For edit mode, changes indicate data
  }, [
    calculateHasAnyData,
    mode,
    currentTab,
    tabRefs,
    getDraftData,
    hasDraftData,
    currentTabHasChanges,
  ]);

  /**
   * Validate the current active tab by calling its submit method.
   * Throws an error if validation fails.
   * Re-throws special error 'CONFLICT_RESOLUTION_REQUIRED' to stop the flow when conflicts need resolution.
   * @param dateRange - Optional date range passed from footer for conflict detection
   */
  const validateCurrentTab = useCallback(
    async (dateRange?: SubmitDateRange): Promise<void> => {
      const currentTabRef = tabRefs[currentTab];
      if (currentTabRef?.current) {
        await currentTabRef.current.submit(dateRange);
      }
    },
    [currentTab, tabRefs]
  );

  /**
   * Get form data from the current active tab.
   * Returns null if no tab is active or tab ref is not available.
   */
  const getCurrentTabFormData = useCallback(() => {
    const currentTabRef = tabRefs[currentTab];
    if (currentTabRef?.current) {
      return currentTabRef.current.getFormData();
    }
    return null;
  }, [currentTab, tabRefs]);

  /**
   * Save current tab data to draft storage.
   * Used for persistence during tab changes in create mode.
   */
  const saveCurrentTabData = useCallback(async (): Promise<void> => {
    const formData = getCurrentTabFormData();
    if (formData) {
      saveDraftData({ tab: currentTab, data: formData });
    }
  }, [getCurrentTabFormData, saveDraftData, currentTab]);

  /**
   * Handle form submission for both create and edit modes.
   *
   * Create mode:
   * 1. Validate current tab
   * 2. Save current tab data to draft
   * 3. Compute & submit merged data (final + current tab)
   * 4. Clear draft on success
   *
   * Edit mode:
   * 1. Validate current tab (with date range for conflict detection)
   * 2. Get current tab form data
   * 3. Submit with entity ID
   *
   * Note: If tab throws 'CONFLICT_RESOLUTION_REQUIRED', the submission is silently stopped
   * to allow the conflict resolution modal to be displayed.
   *
   * @param dateRange - Optional date range passed from footer for conflict detection in Edit/EditChange modes
   */
  const handleFormSubmit = useCallback(
    async (dateRange?: SubmitDateRange): Promise<void> => {
      try {
        if (mode === FeatureMode.Create) {
          // Validate current tab first (no date range needed for create)
          await validateCurrentTab();

          // Get current tab data directly (don't rely on draft state)
          const currentTabData = getCurrentTabFormData();

          // Save current tab data to draft (for state sync, even though we don't use it for submission)
          if (currentTabData) {
            saveDraftData({ tab: currentTab, data: currentTabData });
          }

          // Get draft data and merge with current tab data
          const finalData = getFinalSubmissionData();

          // Override draft data with current tab data to ensure we have latest values
          const mergedData = {
            ...finalData,
            ...(currentTabData ?? {}),
          };

          handleSave({
            data: mergedData,
            successCallback: clearDraft,
          });
        } else if (mode === FeatureMode.Edit || mode === FeatureMode.EditChange) {
          // Edit mode - validate and submit current tab with date range for conflict detection
          await validateCurrentTab(dateRange);
          const formData = getCurrentTabFormData();

          if (formData && entityData) {
            const updateData: TFormData & {
              id: string | number;
              effectiveStartDate?: Date;
              effectiveEndDate?: Date | null;
            } = {
              ...formData,
              id: entityData.id,
            };

            // Add effective dates from date range picker if provided
            if (dateRange) {
              updateData.effectiveStartDate = dateRange.from;
              updateData.effectiveEndDate = dateRange.to;
            }

            handleSave({
              data: updateData,
              successCallback: () => {
                // Success callback will be handled by parent component
                // to reset currentTabHasChanges state
              },
            });
          }
        }
      } catch (error) {
        // Silently ignore flow control errors - these are expected when:
        // - CONFLICT_RESOLUTION_REQUIRED: showing conflict modal
        // - SAVE_COMPLETED: direct save already completed (no need to save again)
        if (
          error instanceof Error &&
          (error.message === SAVE_FLOW_CONTROL_ERRORS.ConflictResolutionRequired ||
            error.message === SAVE_FLOW_CONTROL_ERRORS.SaveCompleted)
        ) {
          return;
        }
        // Re-throw other errors
        throw error;
      }
    },
    [
      mode,
      validateCurrentTab,
      getFinalSubmissionData,
      handleSave,
      clearDraft,
      getCurrentTabFormData,
      entityData,
      saveDraftData,
      currentTab,
    ]
  );

  return {
    hasAnyUnsavedChanges,
    hasAnyData,
    validateCurrentTab,
    getCurrentTabFormData,
    saveCurrentTabData,
    handleFormSubmit,
  };
}
